import {ManageAccountsOutlined as ManageAccountsOutlinedIcon} from '@mui/icons-material';
import {
  AppBar,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {memo} from 'react';

import {APPS, ENVIRONMENTS} from '../apps.jsx';
import NavDivider from './NavDivider.jsx';
import ProductHome from './ProductHome.jsx';
import UserMenu from './UserMenu.jsx';

/**
 * Navigation bar for use as header in Planet apps
 */
export const NavBar = ({
  environment = ENVIRONMENTS.production,
  login,
  logoUrl,
  logout,
  primary,
  productHome,
  secondary,
  sx,
  title,
  user,
  userMenu,
  userMenuProps,
  ...props
}) => {
  const UserMenuComponent = userMenu || (
    <UserMenu user={user} login={login} logout={logout} {...userMenuProps}>
      <MenuItem
        component="a"
        target="_blank"
        rel="noreferrer noopener"
        href={`${APPS.legacyAccount.href[environment]}#/user-settings`}
      >
        <ListItemIcon>
          <ManageAccountsOutlinedIcon />
        </ListItemIcon>
        <ListItemText>My Profile & Settings</ListItemText>
      </MenuItem>
    </UserMenu>
  );

  return (
    <AppBar
      position="static"
      sx={[
        {
          zIndex: theme => theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      <Toolbar variant="dense" disableGutters sx={{paddingX: 1}}>
        {productHome || (
          <ProductHome
            href={logoUrl || APPS.welcomePage.href[environment]}
            title="Insights Platform"
          />
        )}

        {title && <NavDivider />}
        {title && typeof title === 'string' ? (
          <Typography
            variant="subtitle2"
            sx={{paddingX: 1, paddingY: 0.75, color: 'text.primary'}}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        {title && <NavDivider />}
        {primary}

        <Stack
          direction="row"
          sx={{
            gap: 0.5,
            alignItems: 'center',
            marginLeft: 'auto',
          }}
        >
          {secondary}
          {UserMenuComponent}
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

NavBar.propTypes = {
  /**
   App title
  */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * primary area to the right of title
   */
  primary: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   Secondary area
  */
  secondary: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   Logged in User for User Menu
  */
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    sh_user_id: PropTypes.string,
  }),
  /**
   Logout function for User Menu
  */
  logout: PropTypes.func,
  /**
   Login function for User Menu
  */
  login: PropTypes.func,

  /**
   Additional Props for the User Menu
  */
  userMenuProps: PropTypes.object,

  /**
   * Override NavBar styles
   */
  sx: PropTypes.object,

  /**
   * Environment to use for links
   */
  environment: PropTypes.oneOf(Object.values(ENVIRONMENTS)),

  logoUrl: PropTypes.string,

  /**
   * Override ProductHome
   */
  productHome: PropTypes.node,

  /**
   * Override UserMenu
   */
  userMenu: PropTypes.node,
};

// https://github.com/storybookjs/storybook/issues/26720
const MemoNavbar = memo(NavBar);
MemoNavbar.displayName = 'NavBar';

export default MemoNavbar;
